import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from 'app/_services/notification.service';
import { ScheduleValidationService } from 'app/_services/schedule-validation.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import { SiteService } from 'app/_services/site.service';
import { SiteWorkOrderProceduresService } from 'app/_services/site-work-order-procedures.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'create-from-procedure-form-page',
  templateUrl: './create-from-procedure-form-page.component.html',
})
export class CreateFromProcedureFormPageComponent implements OnInit {
  public selectedSiteId: any;
  public selectedProcedure: any;
  public returnUrl: string;
  public allowWorkOrderScheduling: boolean;
  public allowAssetScheduling: boolean;
  public selectedSite: any;

  public saveInProgress = false;

  public form: FormGroup;
  public validationErrors: any[] = [];
  public minDate = new Date();

  public siteAssets: any[] = [];
  public procedureAssets: any = [];
  public assetsLoading = true;
  public siteConfigLoading = true;

  public exemptFromAssetScheduling: boolean;
  public turbineModelOptions: any[] = [];

  constructor(
    private notificationService: NotificationService,
    private workOrderService: SiteWorkOrdersService,
    private procedureService: SiteWorkOrderProceduresService,
    private assetService: SiteAssetsService,
    private siteService: SiteService,
    private scheduleValidationService: ScheduleValidationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
  ) {
  }

  ngOnInit(): void {
    this.selectedSiteId = this.activatedRoute.snapshot.params.id;
    this.returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
    const routeProcedureId = Number(this.activatedRoute.snapshot.params.procedureId);
    this.minDate.setUTCHours(0,0,0,0);

    this.form = this.fb.group({
      asset: new FormControl('', [Validators.required]),
      purpose: new FormControl('', [Validators.required]),
      modelVersion: new FormControl(),
      scheduledStartTime: new FormControl(),
      scheduledEndTime: new FormControl(),
      exemptFromAssetScheduling: new FormControl(),
      overrideApproval: new FormControl(),
    });

    if (!routeProcedureId) {
      this.notificationService.notify('danger', 'Procedure id missing. Please select a procedure again.');
      this.navigateBackToList();
      return;
    }

    this.loadProcedure(routeProcedureId);
  }

  public get pageLoading(): boolean {
    return this.siteConfigLoading || this.assetsLoading;
  }

  public get hasSelectedAsset(): boolean {
    return !!(this.form && this.form.get('asset') && this.form.get('asset').value);
  }

  public get selectedAssetDetails(): any {
    return this.form && this.form.get('asset') ? this.form.get('asset').value : null;
  }

  public get selectedAssetOem(): string {
    return this.normalizeText(this.selectedAssetDetails && this.selectedAssetDetails.manufacturer);
  }

  public get selectedAssetModel(): string {
    return this.normalizeText(this.selectedAssetDetails && this.selectedAssetDetails.model);
  }

  public get selectedAssetRating(): string {
    return this.normalizeText(
      this.selectedAssetDetails && this.selectedAssetDetails.rating
    );
  }

  public get hasSelectedAssetMetadata(): boolean {
    return !!(this.selectedAssetOem || this.selectedAssetModel || this.selectedAssetRating);
  }

  public get hasProcedureModelOptions(): boolean {
    return this.turbineModelOptions.length > 0;
  }

  public get selectedAssetModelMatchesProcedure(): boolean {
    if (!this.selectedAssetModel || !this.hasProcedureModelOptions) {
      return false;
    }

    const selectedAssetModelKey = this.normalizeComparisonKey(this.selectedAssetModel);
    return this.turbineModelOptions.some((option) => this.normalizeComparisonKey(option.value) === selectedAssetModelKey);
  }

  public get showTurbineModelSelection(): boolean {
    return this.hasSelectedAsset
      && this.hasTurbineCompetency()
      && this.turbineModelOptions.length > 1
      && !this.selectedAssetModelMatchesProcedure;
  }

  public get showResolvedTurbineMessage(): boolean {
    return this.hasSelectedAsset
      && this.hasTurbineCompetency()
      && !!this.selectedAssetModel
      && this.selectedAssetModelMatchesProcedure;
  }

  public get turbineModelResolutionMessage(): string {
    if (!this.hasTurbineCompetency() || !this.hasSelectedAsset) {
      return '';
    }

    if (this.showResolvedTurbineMessage) {
      return 'This asset already matches the procedure turbine competency';
    }

    if (this.showTurbineModelSelection && !this.selectedAssetModel) {
      return 'This asset does not have a model recorded, so please confirm the turbine model for this work order';
    }

    if (this.showTurbineModelSelection && !!this.selectedAssetModel && !this.selectedAssetModelMatchesProcedure) {
      return 'This asset model does not match the procedure turbine competency, so please confirm the correct model for this work order';
    }

    return '';
  }

  public get turbineCompetencySummary(): string {
    if (!this.selectedProcedure) {
      return '';
    }

    const turbineCompetency = this.getTurbineProcedureCompetency();
    const competencyName = turbineCompetency
      ? (
        turbineCompetency.competencyTypeShortName ||
        turbineCompetency.shortName ||
        turbineCompetency.competencyTypeName ||
        turbineCompetency.value ||
        turbineCompetency.name ||
        'Turbine'
      )
      : 'Turbine';
    const manufacturer = this.selectedProcedure.manufacturer || (turbineCompetency && (turbineCompetency.manufacturer || turbineCompetency.oem)) || '';
    const models = this.getProcedureTurbineModels();

    const modelText = models.length
      ? `[${models.length > 1 ? models.slice(0, -1).join(', ') + ' or ' + models[models.length - 1] : models[0]}]`
      : '';

    return [competencyName, manufacturer, modelText].filter((item) => !!item).join(' - ');
  }

  private getProcedureTurbineManufacturer(): string {
    const turbineCompetency = this.getTurbineProcedureCompetency();
    return this.normalizeText(
      (this.selectedProcedure && this.selectedProcedure.manufacturer)
      || (turbineCompetency && (turbineCompetency.manufacturer || turbineCompetency.oem))
    );
  }

  private setUpSiteSpecificData() {
    this.siteConfigLoading = true;
    this.siteService.getSiteById(this.selectedSiteId).subscribe(
      (data: any) => {
        this.selectedSite = data;
        this.allowWorkOrderScheduling = data.siteOwner.siteOwnerUser.allowWorkOrderScheduling;
        this.allowAssetScheduling = data.siteOwner.siteOwnerUser.allowAssetScheduling;
        this.getAllSiteAssetsForCreateWorkOrderFromProcedure();
      },
      (error) => {
        this.siteConfigLoading = false;
        throw error;
      },
      () => {
        this.siteConfigLoading = false;
      }
    );
  }

  private loadProcedure(procedureId: number) {
    this.procedureService.getWorkOrderProcedureById(procedureId).subscribe((data: any) => {
      this.selectedProcedure = data;
      this.setUpTurbineModelSelection();
      this.setUpSiteSpecificData();
    }, () => {
      this.notificationService.notify('danger', 'Procedure not found. Please select a procedure again.');
      this.navigateBackToList();
    });
  }

  private getAllSiteAssetsForCreateWorkOrderFromProcedure() {
    this.assetsLoading = true;
    this.siteAssets = [];
    this.assetService.getAllSiteAssetsNoPagination(this.selectedSiteId).subscribe((data: any) => {
      data.forEach((element) => {
        this.siteAssets.push({
          label: element.name,
          value: element.id,
          categories: element.assetCategories.map(c => c.id),
          exemptFromAssetScheduling: element.exemptFromAssetScheduling,
          manufacturer: element.manufacturer,
          model: element.model,
          rating: element.rating,
        });
      });

      this.procedureAssets = this.siteAssets.filter(
        x => this.isAssetCompatibleWithProcedure(x)
      );
      this.assetsLoading = false;
    }, error => {
      this.assetsLoading = false;
      throw error;
    });
  }

  public submit() {
    this.saveInProgress = true;
    const values = this.form.value;

    this.validationErrors = [];
    const validation = this.scheduleValidationService.validateForm(values);

    if (!validation.isValid) {
      this.validationErrors = validation.validationErrors;
      this.saveInProgress = false;
      return
    }

    // if the asset is exempt, this work order exempt tickbox is hidden. send false for consistency
    const workOrderExempt = this.exemptFromAssetScheduling ? false : !!values.exemptFromAssetScheduling;
    const selectedAsset = values.asset;
    const procedureModels = this.getProcedureTurbineModels();
    const procedureRatings = this.getProcedureTurbineRatings();
    const hasModelRequirement = procedureModels.length > 0;
    const hasRatingRequirement = procedureRatings.length > 0;
    const resolvedModel = hasModelRequirement
      ? (
        this.resolveModelValue(values.modelVersion)
        || this.resolveModelValue(selectedAsset && selectedAsset.model)
        || this.resolveModelValue(procedureModels[0])
        || null
      )
      : null;
    const resolvedRating = hasRatingRequirement
      ? (
        this.resolveModelValue(selectedAsset && selectedAsset.rating)
        || this.resolveModelValue(procedureRatings[0])
        || null
      )
      : null;

    const body: any = {
      purpose: values.purpose,
      exemptFromAssetScheduling: workOrderExempt,
      scheduledStartTime: values.scheduledStartTime,
      scheduledEndTime: values.scheduledEndTime,
      overrideApproval: !!values.overrideApproval,
    }
    if (resolvedModel) {
      body.model = resolvedModel;
    }
    if (resolvedRating) {
      body.rating = resolvedRating;
    }
    const assetId = values.asset.value;

    this.workOrderService.createWorkOrderFromProcedure(this.selectedProcedure.id, assetId, body).subscribe(() => {
      this.saveInProgress = false;
      Swal.fire('Created!', 'A new work order has been created.', 'success');

      this.navigateToProcedureList();
    }, error => {
      this.saveInProgress = false;
      throw error;
    });
  }

  public navigateBackToList() {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.navigateToProcedureList();
  }

  private navigateToProcedureList() {
    this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
  }

  public selectedAssetChange(asset) {
    this.exemptFromAssetScheduling = asset.value.exemptFromAssetScheduling;
    this.updateTurbineModelValidation();
    this.prefillModelVersionFromAsset(asset.value);
  }

  private setUpTurbineModelSelection(): void {
    if (!this.hasTurbineCompetency()) {
      this.turbineModelOptions = [];
      this.form.patchValue({ modelVersion: null });
      this.updateTurbineModelValidation();
      return;
    }

    const models = this.getProcedureTurbineModels();
    this.turbineModelOptions = models.map((model) => ({
      label: model,
      value: model,
    }));

    this.updateTurbineModelValidation();
  }

  private prefillModelVersionFromAsset(asset: any): void {
    if (!this.hasTurbineCompetency()) {
      return;
    }

    const assetModel = asset && asset.model ? String(asset.model).trim() : '';
    const assetModelKey = this.normalizeComparisonKey(assetModel);
    const matchingOption = this.turbineModelOptions.find(
      (option) => this.normalizeComparisonKey(option.value) === assetModelKey
    );

    this.form.patchValue({
      modelVersion: matchingOption ? matchingOption.value : null,
    });
  }

  private updateTurbineModelValidation(): void {
    const modelVersionControl = this.form.get('modelVersion');

    if (this.showTurbineModelSelection) {
      modelVersionControl.setValidators([Validators.required]);
    } else {
      modelVersionControl.clearValidators();
    }

    modelVersionControl.updateValueAndValidity();
  }

  private isTurbineCompetency(competency: any): boolean {
    const type = competency.competencyType || '';

    if (type === 'TURBINE') {
      return true;
    }

    return !!(
      competency
      && (
        competency.manufacturer ||
        competency.oem ||
        (Array.isArray(competency.models) && competency.models.length) ||
        (Array.isArray(competency.ratings) && competency.ratings.length)
      )
    );
  }

  private hasTurbineCompetency(): boolean {
    return !!this.getProcedureCompetencies().find((item) => this.isTurbineCompetency(item));
  }

  private getProcedureCompetencies(): any[] {
    return this.selectedProcedure && this.selectedProcedure.competencies ? this.selectedProcedure.competencies : [];
  }

  private getTurbineProcedureCompetency(): any {
    return this.getProcedureCompetencies().find((item) => this.isTurbineCompetency(item));
  }

  private getProcedureTurbineModels(): string[] {
    const turbineCompetency = this.getTurbineProcedureCompetency();
    const competencyModels = this.normalizeStringArray(turbineCompetency && (turbineCompetency.models || turbineCompetency.model));

    if (competencyModels.length) {
      return competencyModels;
    }

    return this.normalizeStringArray(this.selectedProcedure && this.selectedProcedure.models);
  }

  private getProcedureTurbineRatings(): string[] {
    const turbineCompetency = this.getTurbineProcedureCompetency();
    const competencyRatings = this.normalizeStringArray(turbineCompetency && (turbineCompetency.ratings || turbineCompetency.rating));

    if (competencyRatings.length) {
      return competencyRatings;
    }

    return this.normalizeStringArray(this.selectedProcedure && this.selectedProcedure.ratings);
  }

  private isAssetCompatibleWithProcedure(asset: any): boolean {
    if (!asset || !this.selectedProcedure || !this.selectedProcedure.assetCategory) {
      return false;
    }

    if (!Array.isArray(asset.categories) || !asset.categories.includes(this.selectedProcedure.assetCategory.id)) {
      return false;
    }

    if (!this.hasTurbineCompetency()) {
      return true;
    }

    const procedureManufacturer = this.getProcedureTurbineManufacturer();
    const procedureModels = this.getProcedureTurbineModels();
    const procedureRatings = this.getProcedureTurbineRatings();
    const assetManufacturer = this.normalizeText(asset.manufacturer);
    const assetModel = this.normalizeText(asset.model);
    const assetRating = this.normalizeText(asset.rating);

    const procedureManufacturerKey = this.normalizeComparisonKey(procedureManufacturer);
    const assetManufacturerKey = this.normalizeComparisonKey(assetManufacturer);
    const procedureModelKeys = procedureModels.map((model) => this.normalizeComparisonKey(model)).filter((model) => !!model);
    const assetModelKey = this.normalizeComparisonKey(assetModel);
    const procedureRatingKeys = procedureRatings.map((rating) => this.normalizeComparisonKey(rating)).filter((rating) => !!rating);
    const assetRatingKey = this.normalizeComparisonKey(assetRating);

    const matchesManufacturer = !procedureManufacturerKey || !assetManufacturerKey || assetManufacturerKey === procedureManufacturerKey;
    const matchesModel = !procedureModelKeys.length || !assetModelKey || procedureModelKeys.includes(assetModelKey);
    const matchesRating = !procedureRatingKeys.length || !assetRatingKey || procedureRatingKeys.includes(assetRatingKey);

    return matchesManufacturer && matchesModel && matchesRating;
  }

  private normalizeStringArray(values: any): string[] {
    if (Array.isArray(values)) {
      return values
        .map((value) => this.resolveModelValue(value))
        .filter((value) => !!value);
    }

    if (values === null || values === undefined) {
      return [];
    }

    return String(values)
      .split(',')
      .map((value) => this.resolveModelValue(value))
      .filter((value) => value !== '');
  }

  private normalizeText(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  private normalizeComparisonKey(value: any): string {
    return this.normalizeText(value)
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private resolveModelValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value).trim();
    }

    if (typeof value === 'object') {
      const objectValue = value.value || value.model || value.name || value.label;
      if (objectValue !== null && objectValue !== undefined) {
        return String(objectValue).trim();
      }
    }

    return this.normalizeText(value);
  }
}
